import type { ElementType, ReactNode } from "react"
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined"
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined"
import MailOutlinedIcon from "@mui/icons-material/MailOutlined"
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined"
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined"
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined"
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined"
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined"
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined"
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined"
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined"
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined"
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined"
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined"
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined"
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined"
import HubOutlinedIcon from "@mui/icons-material/HubOutlined"
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined"
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined"

type ResourceMeta = {
  label?: string
  icon?: ReactNode
  hide?: boolean
  settingsGroup?: string
  settingsDescription?: string
}

export type AppResource = {
  name: string
  list?: string
  create?: string
  edit?: string
  show?: string
  meta?: ResourceMeta
}

export const APP_RESOURCE_ICON_COMPONENTS = {
  dashboard: DashboardOutlinedIcon,
  opsAccounts: HubOutlinedIcon,
  inquiries: MailOutlinedIcon,
  dispatchResources: TravelExploreOutlinedIcon,
  quotations: RequestQuoteOutlinedIcon,
  orders: ShoppingCartOutlinedIcon,
  customers: PeopleOutlinedIcon,
  suppliers: BusinessOutlinedIcon,
  finance: AccountBalanceOutlinedIcon,
  approvals: ApprovalOutlinedIcon,
  toolbox: BuildCircleOutlinedIcon,
  files: FolderOutlinedIcon,
  playbooks: MenuBookOutlinedIcon,
  insights: PsychologyOutlinedIcon,
  settings: TuneOutlinedIcon,
  settingsExchange: CurrencyExchangeOutlinedIcon,
  settingsPricing: PaidOutlinedIcon,
  settingsDestination: PublicOutlinedIcon,
  settingsCompany: CorporateFareOutlinedIcon,
  settingsUsers: BadgeOutlinedIcon,
  settingsChannels: HubOutlinedIcon,
  settingsDispatchResources: TravelExploreOutlinedIcon,
} satisfies Record<string, ElementType>

function resourceIcon(name: keyof typeof APP_RESOURCE_ICON_COMPONENTS) {
  const Icon = APP_RESOURCE_ICON_COMPONENTS[name]
  return <Icon />
}

export const appResources: AppResource[] = [
  { name: "dashboard", list: "/dashboard", meta: { label: "经营总览", icon: resourceIcon("dashboard") } },
  // 运营中心组(app-sider slice 1-4):账号数据 + 询单中心 + 计调资源 —— 顺序即分组,改这里要同步 app-sider 切片
  { name: "ops-accounts", list: "/dashboard/operations/accounts", show: "/dashboard/operations/accounts/:id", meta: { label: "账号数据", icon: resourceIcon("opsAccounts") } },
  { name: "inquiries", list: "/dashboard/inquiries", show: "/dashboard/inquiries/d/:dest", meta: { label: "询单中心", icon: resourceIcon("inquiries") } },
  { name: "dispatch-resources", list: "/dashboard/operations/resources", meta: { label: "计调资源", icon: resourceIcon("dispatchResources") } },
  { name: "quotations", list: "/dashboard/quotations", meta: { label: "方案与报价", icon: resourceIcon("quotations") } },
  { name: "orders", list: "/dashboard/orders", meta: { label: "订单执行", icon: resourceIcon("orders") } },
  { name: "customers", list: "/dashboard/customers", meta: { label: "客户中心", icon: resourceIcon("customers") } },
  { name: "suppliers", list: "/dashboard/suppliers", meta: { label: "供应链中心", icon: resourceIcon("suppliers") } },
  { name: "finance", list: "/dashboard/finance", meta: { label: "财务中心", icon: resourceIcon("finance") } },
  { name: "approvals", list: "/dashboard/approvals", meta: { label: "审批中心", icon: resourceIcon("approvals") } },
  { name: "toolbox", list: "/dashboard/toolbox", meta: { label: "工具箱", icon: resourceIcon("toolbox") } },
  { name: "files", list: "/dashboard/files", meta: { label: "文件中心", icon: resourceIcon("files") } },
  { name: "playbooks", list: "/dashboard/playbooks", meta: { label: "定制师手册", icon: resourceIcon("playbooks") } },
  { name: "insights", list: "/dashboard/insights", show: "/dashboard/insights/:id", meta: { hide: true, label: "认知库", icon: resourceIcon("insights") } },
  {
    name: "users",
    list: "/dashboard/users",
    create: "/dashboard/users/create",
    edit: "/dashboard/users/edit/:id",
    meta: { hide: true, label: "用户管理" },
  },
  {
    name: "departments",
    meta: { hide: true, label: "部门" },
  },
  {
    name: "settings_exchange",
    list: "/dashboard/settings/exchange",
    meta: {
      hide: true,
      label: "汇率参数",
      icon: resourceIcon("settingsExchange"),
      settingsGroup: "工具箱",
      settingsDescription: "工具箱汇率工具的数据来源与缓存策略。",
    },
  },
  {
    name: "settings_pricing",
    list: "/dashboard/settings/pricing",
    meta: {
      hide: true,
      label: "报价参数",
      icon: resourceIcon("settingsPricing"),
      settingsGroup: "工具箱",
      settingsDescription: "工具箱报价核算的项目分类、佣金模板与外网价提醒规则。",
    },
  },
  {
    name: "settings_destination",
    list: "/dashboard/settings/destination",
    meta: {
      hide: true,
      label: "目的地参数",
      icon: resourceIcon("settingsDestination"),
      settingsGroup: "工具箱",
      settingsDescription: "工具箱目的地工具的热门国家分组与业务别名。",
    },
  },
  {
    name: "settings_company",
    list: "/dashboard/settings/company",
    meta: {
      hide: true,
      label: "公司主体与账户",
      icon: resourceIcon("settingsCompany"),
      settingsGroup: "财务中心",
      settingsDescription: "财务中心公司收款主体与对公银行账户的集中维护。",
    },
  },
  {
    name: "settings_users",
    list: "/dashboard/settings/users",
    meta: {
      hide: true,
      label: "用户参数",
      icon: resourceIcon("settingsUsers"),
      settingsGroup: "用户管理",
      settingsDescription: "岗位、角色与组织部门的集中维护。",
    },
  },
  {
    name: "settings_channels",
    list: "/dashboard/settings/channels",
    meta: {
      hide: true,
      label: "渠道账号",
      icon: resourceIcon("settingsChannels"),
      settingsGroup: "运营中心",
      settingsDescription: "账号数据与询单中心共用的渠道类型、渠道账号集中维护。",
    },
  },
  {
    name: "settings_dispatch_resources",
    list: "/dashboard/settings/dispatch-resources",
    meta: {
      hide: true,
      label: "计调资源",
      icon: resourceIcon("settingsDispatchResources"),
      settingsGroup: "运营中心",
      settingsDescription: "维护计调目的地，供询单记录和行程方案共用。",
    },
  },
]

// 系统参数分组的展示顺序 = 一级导航的排列序列(运营中心 → 结算与管控 → 支持与工具/工具箱 → 系统管理),不在表内的排末尾
const SETTINGS_GROUP_ORDER = ["运营中心", "财务中心", "工具箱", "用户管理"]

export const settingsResource: AppResource = {
  name: "settings",
  list: "/dashboard/settings",
  meta: { hide: true, label: "系统参数", icon: resourceIcon("settings") },
}

export function getSettingsResources() {
  return appResources.filter((resource) => resource.meta?.settingsGroup && resource.list)
}

export function getSettingsResourceGroups() {
  const groups = new Map<string, AppResource[]>()

  getSettingsResources().forEach((resource) => {
    const groupTitle = resource.meta?.settingsGroup
    if (!groupTitle) return

    const items = groups.get(groupTitle) ?? []
    items.push(resource)
    groups.set(groupTitle, items)
  })

  // 分组顺序 = 一级导航排列(SETTINGS_GROUP_ORDER),保证 settings 落地页与左侧 sider 一致;不在表内排末尾
  return Array.from(groups.entries())
    .map(([key, items]) => {
      const idx = SETTINGS_GROUP_ORDER.indexOf(key)
      return { key, title: key, items, order: idx === -1 ? Number.MAX_SAFE_INTEGER : idx }
    })
    .sort((a, b) => a.order - b.order)
    .map(({ key, title, items }) => ({ key, title, items }))
}
